import React, { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { BsFillEyeFill, BsFillEyeSlashFill } from "react-icons/bs";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ModalComponent from "../../components/Modals/Modal";
import {
  forgotPassword,
  verifyCodeAction,
  resetPassword,
} from "../../store/actions/forgotPasswordActions";

const ForgotPassword = () => {
  // status
  const [status, setstatus] = useState("forgot");
  // verification code
  const [verificationcode, setverificationcode] = useState();
  //history for routing
  const history = useNavigate();
  // dispatch actions
  const dispatch = useDispatch();
  // resend countdown (seconds)
  const [resendCountdown, setResendCountdown] = useState(0);
  // error from API
  const [errorFromAPI, seterrorFromAPI] = useState("");

  // loader for API calls
  const [loading, setLoading] = useState(false);

  // modal for errors and messages (reuse same modal as signup/login)
  const [modalFields, setModalFields] = useState({
    open: false,
    text: "",
    btnText1: "Ok",
    closeFunc: () => {
      setModalFields((prev) => ({ ...prev, open: false }));
    },
  });

  // show modal when errorFromAPI is set
  React.useEffect(() => {
    try {
      if (errorFromAPI) {
        setModalFields((prev) => ({ ...prev, open: true, text: errorFromAPI }));
        seterrorFromAPI("");
      }
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }, [errorFromAPI]);

  // start countdown when entering verify state
  useEffect(() => {
    if (status === "verify") {
      // start 60s cooldown when verification screen appears
      setResendCountdown(60);
    }
  }, [status]);

  // countdown interval
  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const id = setInterval(() => {
      setResendCountdown((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCountdown]);

  const handleResendCode = () => {
    try {
      if (!email) {
        setModalFields((prev) => ({ ...prev, open: true, text: "Email missing" }));
        return;
      }
      if (resendCountdown > 0) return; // still cooling down
      setLoading(true);
      // call same forgotPassword API to resend code
      dispatch(forgotPassword(email, setstatus, seterrorFromAPI, setLoading));
      setResendCountdown(60);
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  };

  // email
  const [email, setemail] = useState("");
  const [emailActive, setemailActive] = useState(false);
  //password
  const [password, setpassword] = useState("");
  const [passwordStrength, setpasswordStrength] = useState(0);
  // show/hide password toggles
  const [showPassword, setShowPassword] = useState("password");
  const [showPassword2, setShowPassword2] = useState("password");
  //confirmPass
  const [confirmPassword, setconfirmPassword] = useState("");
  // check password strength
  const handleSetPassword = (e) => {
    try {
      let strengthLevel = 0;
      const passArray = [...e];
      setpassword(e);
      if (e !== 0) {
        const hasLowerCase = passArray?.filter(
          (letter) => letter >= "a" && letter <= "z",
        );
        const hasUpperCase = passArray?.filter(
          (letter) => letter >= "A" && letter <= "Z",
        );
        const hasNumber = passArray?.filter(
          (letter) => letter >= "0" && letter <= "9",
        );
        const hasSC1 = passArray?.filter(
          (letter) => letter >= "!" && letter <= "/",
        );
        const hasSC2 = passArray?.filter(
          (letter) => letter >= ":" && letter <= "@",
        );
        const hasSC3 = passArray?.filter(
          (letter) => letter >= "[" && letter <= "`",
        );
        const hasSC4 = passArray?.filter(
          (letter) => letter >= "{" && letter <= "~",
        );
        if (hasLowerCase?.length > 0) {
          strengthLevel = strengthLevel + 20;
        }
        if (hasUpperCase?.length > 0) {
          strengthLevel = strengthLevel + 20;
        }
        if (hasNumber?.length > 0) {
          strengthLevel = strengthLevel + 20;
        }
        if (
          hasSC1?.length > 0 ||
          hasSC2?.length > 0 ||
          hasSC3?.length > 0 ||
          hasSC4?.length > 0
        ) {
          strengthLevel = strengthLevel + 20;
        }
        if (e?.length > 5) {
          strengthLevel = strengthLevel + 20;
        }
      }
      setpasswordStrength(strengthLevel);
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };
  // forgot password
  const handleForgotPassword = () => {
    try {
      if (email) {
        setLoading(true);
        dispatch(forgotPassword(email, setstatus, seterrorFromAPI, setLoading));
      } else {
        setModalFields((prev) => ({ ...prev, open: true, text: "Please fill all the fields" }));
      }
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };
  const handleVerifyCode = () => {
    try {
      if (verificationcode) {
        setLoading(true);
        dispatch(
          verifyCodeAction(
            email,
            verificationcode,
            setstatus,
            seterrorFromAPI,
            setLoading,
          ),
        );
      } else {
        setModalFields((prev) => ({ ...prev, open: true, text: "Please enter the verification code." }));
      }
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const handleResetPassword = () => {
    try {
      if (password && confirmPassword) {
        if (password === confirmPassword) {
          if (passwordStrength === 100) {
            setLoading(true);
            dispatch(
              resetPassword(
                email,
                password,
                history,
                setstatus,
                seterrorFromAPI,
                setLoading,
              ),
            );
          } else {
            setModalFields((prev) => ({ ...prev, open: true, text: "Your password must contain at least 6 characters. It should include a mix of upper and lower case letters, numbers, and special characters." }));
          }
        } else {
          setModalFields((prev) => ({ ...prev, open: true, text: "Passwords do not match" }));
        }
      } else {
        setModalFields((prev) => ({ ...prev, open: true, text: "Please fill all the fields." }));
      }
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };
  const toggleShowPassword = () => {
    if (showPassword === "text") setShowPassword("password");
    else setShowPassword("text");
  };
  const toggleShowPassword2 = () => {
    if (showPassword2 === "text") setShowPassword2("password");
    else setShowPassword2("text");
  };
  return (
    <div
      className="flex items-start justify-center px-4 w-full pt-6"
      style={{ minHeight: "calc(100vh - 60px)" }}
    >
      <div className="w-full max-w-container mx-auto">
        {status === "forgot" ? (
          <>
            <div className="flex flex-col items-start gap-1 mb-4">
              <h1 className="text-[#211F59] text-2xl font-semibold">
                Forgot Password?
              </h1>
              <div className="text-[14px] text-[#211F59] font-semibold">
                Enter your email address you're using for your account below and
                we will send you a password reset link.
              </div>
            </div>

            <div className="mb-2">
              <div className="bg-[#E6E6E6] rounded-[12px] h-[48px] flex items-center">
                <input
                  placeholder="Email"
                  value={email}
                  onFocus={() => setemailActive(true)}
                  onChange={(e) => setemail(e?.target?.value)}
                  className="w-full bg-transparent placeholder:text-[#373559] placeholder:text-[14px] pl-[13.5px] h-[48px] rounded-[12px] border-[1px] border-transparent focus:border-[#7876A1] outline-none"
                />
              </div>
              {/* removed inline prompt; button is disabled when email is empty */}
            </div>

            <ModalComponent
              text={modalFields.text}
              openState={modalFields.open}
              closeFunction={() => {
                setModalFields({ ...modalFields, open: false });
              }}
              btnText1={modalFields.btnText1}
              btnClickFunc1={modalFields.closeFunc}
            />

            <div className="mt-4 space-y-3">
              <button
                onClick={handleForgotPassword}
                disabled={loading || !email}
                aria-busy={loading}
                className="w-full bg-[#34318C] text-white rounded-[15px] h-[48px] px-6 flex items-center justify-center disabled:opacity-60"
              >
                {loading ? (
                  <Box sx={{ display: "flex" }}>
                    <CircularProgress size={18} color="inherit" />
                  </Box>
                ) : (
                  "Request Password Reset"
                )}
              </button>

              {/* Button For Every Main Button */}
              <div className="flex items-center mt-3 text-[12px]  text-center justify-between gap-2">
                <span className="text-[#7876A1]">
                  Back to{" "}
                  <span
                    className=" font-bold underline cursor-pointer"
                    onClick={() => history("/Login")}
                  >
                    Sign in
                  </span>
                </span>

                <span>
                  Don’t have an account?{" "}
                  <span
                    className="text-[#211F59] font-bold underline cursor-pointer"
                    onClick={() => history("/Signup")}
                  >
                    Sign up
                  </span>
                </span>
              </div>



            </div>
          </>
        ) : status === "verify" ? (
          <>
            <div className="flex flex-col items-start gap-1 mb-4">
              <h1 className="text-[#211F59] text-2xl font-semibold">
                Forgot Password?
              </h1>
              <div className="text-[13px] text-[#211F59] font-semibold">
                A verification code has been sent to {email}.
              </div>
            </div>

            <div className="mb-2">
              <div className="bg-[#E6E6E6] rounded-[12px] h-[48px] flex items-center">
                <input
                  placeholder="Code"
                  value={verificationcode ? verificationcode : ""}
                  onChange={(e) => setverificationcode(e?.target?.value)}
                  className="w-full bg-transparent placeholder:text-[#373559] placeholder:text-[14px] pl-[13.5px] h-[48px] rounded-[12px] border-[1px] border-transparent focus:border-[#7876A1] outline-none"
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-[13px]">
                <div />
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading || resendCountdown > 0}
                  className={`text-[#211F59] font-medium underline ${loading || resendCountdown > 0 ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend code"}
                </button>
              </div>
            </div>

            <ModalComponent
              text={modalFields.text}
              openState={modalFields.open}
              closeFunction={() => {
                setModalFields({ ...modalFields, open: false });
              }}
              btnText1={modalFields.btnText1}
              btnClickFunc1={modalFields.closeFunc}
            />

            <div className="mt-4 space-y-3">
              <button
                onClick={handleVerifyCode}
                disabled={loading}
                aria-busy={loading}
                className="w-full bg-[#34318C] text-white rounded-[15px] h-[48px] px-6 flex items-center justify-center"
              >
                {loading ? (
                  <Box sx={{ display: "flex" }}>
                    <CircularProgress size={18} color="inherit" />
                  </Box>
                ) : (
                  "Verify"
                )}
              </button>
              <div className="text-center text-[14px]">
                <span
                  className="text-[#211F59] font-medium underline cursor-pointer"
                  onClick={() => {
                    setstatus("forgot");
                    setverificationcode("");
                    setemail("");
                    seterrorFromAPI("");
                  }}
                >
                  Change email
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-start gap-1 mb-4">
              <h1 className="text-[#211F59] text-2xl font-semibold">
                Reset Password
              </h1>
            </div>

            <div className="space-y-3 mb-2">
              <div className="relative">
                <div className="bg-[#E6E6E6] rounded-[12px] h-[48px] flex items-center">
                  <input
                    placeholder="Password"
                    type={showPassword}
                    value={password}
                    onChange={(e) => handleSetPassword(e?.target?.value)}
                    className="w-full bg-transparent placeholder:text-[#373559] placeholder:text-[14px] pl-[13.5px] pr-12 h-[48px] rounded-[12px] border-[1px] border-transparent focus:border-[#7876A1] outline-none"
                  />
                </div>
                <button
                  onClick={() => toggleShowPassword()}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B5472]"
                  aria-label="toggle password visibility"
                  type="button"
                >
                  {showPassword === "password" ? (
                    <BsFillEyeFill size={18} />
                  ) : (
                    <BsFillEyeSlashFill size={18} />
                  )}
                </button>
              </div>

              <div className="relative">
                <div className="bg-[#E6E6E6] rounded-[12px] h-[48px] flex items-center">
                  <input
                    placeholder="Confirm password"
                    type={showPassword2}
                    value={confirmPassword}
                    onChange={(e) => setconfirmPassword(e?.target?.value)}
                    className="w-full bg-transparent placeholder:text-[#373559] placeholder:text-[14px] pl-[13.5px] pr-12 h-[48px] rounded-[12px] border-[1px] border-transparent focus:border-[#7876A1] outline-none"
                  />
                </div>
                <button
                  onClick={() => toggleShowPassword2()}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B5472]"
                  aria-label="toggle confirm password visibility"
                  type="button"
                >
                  {showPassword2 === "password" ? (
                    <BsFillEyeFill size={18} />
                  ) : (
                    <BsFillEyeSlashFill size={18} />
                  )}
                </button>
              </div>
            </div>

            <ModalComponent
              text={modalFields.text}
              openState={modalFields.open}
              closeFunction={() => {
                setModalFields({ ...modalFields, open: false });
              }}
              btnText1={modalFields.btnText1}
              btnClickFunc1={modalFields.closeFunc}
            />

            <div className="mt-4 space-y-3">
              <button
                onClick={handleResetPassword}
                disabled={loading}
                aria-busy={loading}
                className="w-full bg-[#34318C] text-white rounded-[15px] h-[48px] px-6 flex items-center justify-center"
              >
                {loading ? (
                  <Box sx={{ display: "flex" }}>
                    <CircularProgress size={18} color="inherit" />
                  </Box>
                ) : (
                  "Reset Password"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
